import CustomerOrderPaymentsApiV2 from 'api/v2/admins/customer-order-payments-api-v2'
import GenericModal from 'components/organisms/generic-modal'
import { ALERT_TYPES } from 'constants/alert-constants'
import CardLoarder from 'components/organisms/card-loader'
import CustomerOrderPaymentForm from 'domains/customer-order-payment/organisms/form/customer-order-payment-form'
import PaymentService from 'domains/customer-order-payment/services/customer-order-payment-service'
import CustomerOrderPaymentView from 'domains/customer-order/molecules/payment-view'
import CustomerOrderPaymentUpdateStatusForm from 'domains/customer-order/organisms/payment-update-status-form'
import OrderService from 'domains/customer-order/services/customer-order-service'
import PropTypes from 'prop-types'
import withLoader from 'hocs/with-loader'
import { useHistory } from 'react-router'
import React, { useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  Alert
} from 'reactstrap'
import ParamsUtil from 'utils/params-util'

const propTypes = {
  order: PropTypes.shape({
    number   : PropTypes.string,
    payments : PropTypes.array
  }),
  isLoading : PropTypes.bool,
  setOrder  : PropTypes.func,
  pageUtils : PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  }),
  setRefetchOrder: PropTypes.func
}

const CustomerOrderDetailPaymentPage = ({
  order,
  isLoading,
  setOrder,
  pageUtils,
  setRefetchOrder
}) => {
  const togglePaymentForm                           = () => setIsPaymentFormOpen((prevState) => !prevState)
  const [ chosenPayment, setChosenPayment ]         = useState(undefined)
  const [ isPaymentFormOpen, setIsPaymentFormOpen ] = useState(false)

  const history = useHistory()

  const handleClickUpdateStatusButton = (payment) => {
    setChosenPayment(payment)
    togglePaymentForm()
  }

  // Updates Payment status
  const handleSubmitPaymentUpdate = async (values, formikBag) => {
    formikBag.setSubmitting(true)
    const cleanedValues = ParamsUtil.removeEmptyAttributes(values)
    await CustomerOrderPaymentsApiV2.update(cleanedValues)
      .then((response) => {
        const updatedPayment = response.data.payment
        const newPayments    = updatePaymentState(updatedPayment)

        setOrder({
          ...order,
          payments: [ ...newPayments ]
        })
        setRefetchOrder(true)
        formikBag.resetForm()
        togglePaymentForm()
        pageUtils.setAlertMsg('Payment has been updated', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => formikBag.setSubmitting(false))
  }

  // Creates a new Payment with Bank Transfer
  const handleSubmitPaymentCreate = async (values, formikBag) => {
    formikBag.setSubmitting(true)
    CustomerOrderPaymentsApiV2.create(values)
      .then((response) => {
        const newPayment    = response.data.payment
        let updatedPayments = [ ...order.payments ]
        updatedPayments.push(newPayment)
        setOrder({
          ...order,
          payments: [ ...updatedPayments ]
        })

        formikBag.resetForm()
        pageUtils.setAlertMsg('Payment has been updated', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => formikBag.setSubmitting(false))
  }

  const isPaymentCreatable = OrderService.isPaymentCreatable(order)

  // Helper to update payment array in state
  const updatePaymentState = (updatedPayment) => {
    const updateIndex        = order.payments.findIndex((orderPay) => orderPay.id === updatedPayment.id)
    let newPayments          = [ ...order.payments ]
    newPayments[updateIndex] = updatedPayment
    return newPayments
  }

  /** Customer Order Payment with card loader */
  const OrderPaymentsListWithLoader = withLoader(() => (
    order && order.payments.map((payment) => (
      <Card
        key={ `customer-order-payment-${payment.id}` }
      >
        <CustomerOrderPaymentView
          customerOrderPayment={ payment }
        />
        <CardBody>
          <Button
            outline
            color="primary"
            onClick={ () => handleClickUpdateStatusButton(payment) }
          >
            { 'Update Status Manually' }
          </Button>
          {
            PaymentService.isBankTransferPendingSettlement(payment)
              ? (
                <Button
                  outline
                  color='primary'
                  onClick={ () => {
                    history.push(`/app/finance/orders/payments/bank_transfer/${payment.payment_method_transaction_no}`)
                  } }
                >
                  { 'Confirm Bank Transfer' }
                </Button>
              )
              : null
          }
        </CardBody>
      </Card>
    ))
  ), CardLoarder)

  return (
    <>

      <p>
        This page lists all the payments for an <strong>Order</strong><br/>
        An <strong>Order</strong> can have one or many <strong>Payments</strong>.<br/>
        The <strong>active Payment</strong> is always the <strong>latest/last</strong> Payment in the list.<br/>
        At any one time, an Order should only have <strong>at most 1 Payment</strong> with a status of PENDING, PENDING_SETTLEMENT or COMPLETED
      </p>

      <h3>{ 'Payments' }</h3>
      <OrderPaymentsListWithLoader
        isLoading={ isLoading }
      />

      {
        isPaymentCreatable
          ? (
            <Card body>
              <CustomerOrderPaymentForm
                onSubmit={ handleSubmitPaymentCreate }
                order={ order }
              />
            </Card>
          )
          : <Alert color='info'>
            <i className='fas fa-info-circle mr-3' />
            <span>
              { 'You can only create Payment for this Order if:' }
            </span>
            <ul className='mb-0'>
              <li>
                { 'Order.status is PENDING' }
              </li>
              <li>
                { 'Payment.status is EXPIRED, CANCELLED or FAILED.' }
              </li>
            </ul>
          </Alert>
      }

      <GenericModal
        isOpen={ isPaymentFormOpen }
        toggle={ togglePaymentForm }
        title={ 'Update Payment Status' }
      >
        <CustomerOrderPaymentView
          customerOrderPayment={ chosenPayment }
        />
        <CustomerOrderPaymentUpdateStatusForm
          payment={ chosenPayment }
          onSubmit={ handleSubmitPaymentUpdate }
        />
      </GenericModal>
    </>
  )
}

CustomerOrderDetailPaymentPage.propTypes = propTypes

export default CustomerOrderDetailPaymentPage
